import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Banner,
  Card,
  MarkdownBody,
  SectionTitle,
  SlaBadge,
  ageLabel,
  formatBytes,
} from "../components/ui";
import { uploadToSignedUrl, type Api, type TicketAttachmentRef } from "../lib/api";
import { MentionTextarea } from "../components/MentionTextarea";
import {
  TICKET_CATEGORIES,
  TICKET_PRIORITIES,
  type KbArticle,
  type NotificationPref,
  type Ticket,
  type TicketStatus,
} from "../lib/types";

const STATUS_LABEL: Record<TicketStatus, string> = {
  open: "Open",
  pending: "Pending",
  in_progress: "In progress",
  resolved: "Resolved",
  closed: "Closed",
};

function statusBadge(status: TicketStatus): string {
  return status === "open" || status === "in_progress" ? "badge-accent" : "badge";
}

function slaTone(state?: Ticket["slaState"]): string {
  if (state === "breached") return "text-danger";
  if (state === "at_risk") return "text-amber-500";
  return "text-ink-muted";
}

function dueLabel(iso?: string): string {
  if (!iso) return "Not set";
  const due = new Date(iso).getTime();
  const diff = due - Date.now();
  if (!Number.isFinite(diff)) return "Not set";
  if (diff <= 0) return `${ageLabel(iso)} overdue`;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return `${minutes}m remaining`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h remaining`;
  return `${Math.floor(hours / 24)}d remaining`;
}

export function Tickets({ api, meEmail }: { api: Api; meEmail: string }) {
  const [tickets, setTickets] = useState<Ticket[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Ticket | null>(null);
  const [creating, setCreating] = useState(false);
  const [query, setQuery] = useState("");
  const [searchBusy, setSearchBusy] = useState(false);
  const [prefs, setPrefs] = useState<NotificationPref | null>(null);
  const [prefsBusy, setPrefsBusy] = useState(false);
  const [prefsError, setPrefsError] = useState<string | null>(null);
  const [prefsSaved, setPrefsSaved] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep link from a notification email: /app/tickets?ticket=<id> opens that case directly.
  // An explicit list selection takes precedence; falling back to the URL param lets the
  // emailed link land on the exact thread. §1.4 / F-1
  const deepLinkId = searchParams.get("ticket");
  const activeTicketId = selected?.id ?? deepLinkId;

  async function loadTickets(q = "") {
    setError(null);
    setSearchBusy(true);
    try {
      if (q.trim() === "") {
        const { tickets } = await api.listTickets();
        setTickets(tickets);
      } else {
        const { tickets } = await api.searchTickets(q.trim());
        setTickets(tickets);
      }
    } catch {
      setError("Could not load your support tickets.");
    } finally {
      setSearchBusy(false);
    }
  }

  async function loadPrefs() {
    setPrefsError(null);
    try {
      const { prefs } = await api.getNotificationPrefs();
      setPrefs(prefs);
    } catch {
      setPrefsError("Could not load notification preferences.");
    }
  }

  async function savePrefs() {
    if (!prefs) return;
    setPrefsBusy(true);
    setPrefsError(null);
    setPrefsSaved(false);
    try {
      const { prefs: next } = await api.setNotificationPrefs({
        enabled: prefs.enabled,
      });
      setPrefs(next);
      setPrefsSaved(true);
    } catch {
      setPrefsError("Could not save notification preferences.");
    } finally {
      setPrefsBusy(false);
    }
  }

  useEffect(() => {
    void loadTickets();
    void loadPrefs();
  }, [api]);

  if (activeTicketId) {
    return (
      <TicketDetail
        api={api}
        meEmail={meEmail}
        ticketId={activeTicketId}
        onBack={() => {
          setSelected(null);
          // Drop the deep-link param so returning to the list doesn't immediately reopen it.
          if (deepLinkId) {
            const next = new URLSearchParams(searchParams);
            next.delete("ticket");
            setSearchParams(next, { replace: true });
          }
          void loadTickets(query);
        }}
      />
    );
  }

  return (
    <div>
      <SectionTitle
        kicker="Support · Govern"
        title="Support tickets"
        desc="Open and track support cases with PumaMesh. Cases are shared across everyone on your account. Everything you share here — including attached logs — stays in PumaMesh's US-owned, US-hosted storage."
      />

      {error && (
        <div className="mb-6">
          <Banner tone="error">{error}</Banner>
        </div>
      )}

      <div className="mb-6 grid gap-4 lg:grid-cols-[1fr_auto]">
        <Card className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[14rem] flex-1">
              <label className="label">Search tickets</label>
              <input
                className="input"
                value={query}
                onChange={(e) => {
                  const next = e.target.value;
                  setQuery(next);
                  if (next.trim() === "") void loadTickets("");
                }}
                placeholder="Search subject, description, comments"
              />
            </div>
            <button className="btn-primary" disabled={searchBusy} onClick={() => void loadTickets(query)}>
              {searchBusy ? "Searching…" : "Search"}
            </button>
            <button
              className="btn-ghost"
              disabled={searchBusy || query.trim() === ""}
              onClick={() => {
                setQuery("");
                void loadTickets("");
              }}
            >
              Clear
            </button>
          </div>
        </Card>

        <Card className="p-4">
          <div className="mb-2 text-sm font-semibold text-ink">Notifications</div>
          {prefsError && <p className="mb-2 text-xs text-danger">{prefsError}</p>}
          {prefs ? (
            <>
              <label className="mb-2 flex items-center gap-2 text-sm text-ink">
                <input
                  type="checkbox"
                  checked={prefs.enabled}
                  disabled={prefsBusy}
                  onChange={(e) =>
                    setPrefs((cur) =>
                      cur
                        ? {
                            ...cur,
                            enabled: e.target.checked,
                          }
                        : cur,
                    )
                  }
                />
                Email notifications enabled
              </label>
              <p className="mb-2 text-xs text-ink-dim">
                Notifications are sent as they happen (replies, status changes, and @mentions on
                your cases).
              </p>
              <div className="mt-3 flex items-center justify-end gap-2">
                {prefsSaved && <span className="text-xs text-ink-dim">Saved</span>}
                <button className="btn-primary" disabled={prefsBusy} onClick={() => void savePrefs()}>
                  {prefsBusy ? "Saving…" : "Save"}
                </button>
              </div>
            </>
          ) : (
            <p className="text-sm text-ink-muted">Loading…</p>
          )}
        </Card>
      </div>

      <div className="mb-6 flex items-center justify-between">
        <p className="text-sm text-ink-muted">
          {tickets ? `${tickets.length} ticket${tickets.length === 1 ? "" : "s"}` : "Loading…"}
        </p>
        <button className="btn-primary" onClick={() => setCreating((v) => !v)}>
          {creating ? "Cancel" : "New ticket"}
        </button>
      </div>

      {creating && (
        <div className="mb-6">
          <CreateTicket
            api={api}
            onCreated={(t) => {
              setCreating(false);
              setTickets((cur) => (cur ? [t, ...cur] : [t]));
              setSelected(t);
            }}
          />
        </div>
      )}

      {tickets && tickets.length === 0 && !creating && (
        <Card>
          <p className="text-sm text-ink-muted">
            You have no support tickets yet. Open one to get help from PumaMesh support.
          </p>
        </Card>
      )}

      <div className="grid gap-3">
        {tickets?.map((t) => (
          <Card key={t.id} className="cursor-pointer transition hover:border-accent/50">
            <button className="w-full text-left" onClick={() => setSelected(t)}>
              <div className="flex flex-wrap items-center gap-2">
                <span className={statusBadge(t.status)}>{STATUS_LABEL[t.status]}</span>
                <span className="badge">{t.priority}</span>
                <span className="badge">{t.category}</span>
                <SlaBadge state={t.slaState} />
                <span className="badge">{ageLabel(t.createdAt)} old</span>
                {t.number !== undefined && <span className="badge">Case #{t.number}</span>}
                <span className="text-base font-semibold text-ink">{t.subject}</span>
              </div>
              {t.tags && t.tags.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {t.tags.map((tag) => (
                    <span key={tag} className="badge">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
              <div className="mt-1 text-xs text-ink-dim">
                Opened {new Date(t.createdAt).toLocaleString()} by{" "}
                {t.requesterEmail === meEmail ? "you" : t.requesterEmail} · updated{" "}
                {new Date(t.updatedAt).toLocaleString()} · {t.comments.length} repl
                {t.comments.length === 1 ? "y" : "ies"}
              </div>
            </button>
          </Card>
        ))}
      </div>
    </div>
  );
}

function CreateTicket({ api, onCreated }: { api: Api; onCreated: (t: Ticket) => void }) {
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<string>("technical");
  const [priority, setPriority] = useState<string>("normal");
  const [attachments, setAttachments] = useState<TicketAttachmentRef[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<KbArticle[]>([]);
  const submittedRef = useRef(false);
  const shownRef = useRef<string[]>([]);

  // KB deflection: suggest self-help articles as the user types (debounced). §4
  useEffect(() => {
    const q = `${subject} ${description}`.trim();
    const handle = window.setTimeout(() => {
      api
        .getKbSuggestions(q)
        .then((r) => {
          setSuggestions(r.articles);
          shownRef.current = r.articles.map((a) => a.id);
        })
        .catch(() => setSuggestions([]));
    }, 300);
    return () => window.clearTimeout(handle);
  }, [api, subject, description]);

  // If the user viewed suggestions and left WITHOUT opening a case, record a deflection. §4
  useEffect(() => {
    return () => {
      if (!submittedRef.current && shownRef.current.length > 0) {
        void api.recordKbDeflection(shownRef.current).catch(() => {});
      }
    };
  }, [api]);

  async function submit() {
    if (subject.trim() === "" || description.trim() === "") {
      setError("Subject and description are required.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { ticket } = await api.createTicket({
        subject,
        description,
        category,
        priority,
        attachments,
      });
      submittedRef.current = true;
      onCreated(ticket);
    } catch {
      setError("Could not create the ticket. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <h2 className="mb-4 text-lg font-semibold text-ink">Open a support ticket</h2>
      {error && (
        <div className="mb-4">
          <Banner tone="error">{error}</Banner>
        </div>
      )}
      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-ink-dim">
            Subject
          </label>
          <input
            className="input"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Short summary of the issue"
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-ink-dim">
              Category
            </label>
            <select className="input" value={category} onChange={(e) => setCategory(e.target.value)}>
              {TICKET_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-ink-dim">
              Priority
            </label>
            <select className="input" value={priority} onChange={(e) => setPriority(e.target.value)}>
              {TICKET_PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-ink-dim">
            Description
          </label>
          <textarea
            className="input min-h-[120px]"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe what happened, what you expected, and any steps to reproduce."
          />
        </div>
        {suggestions.length > 0 && (
          <div className="rounded-sm border border-accent/30 bg-accent/5 p-3">
            <div className="mb-2 text-xs font-medium uppercase tracking-wider text-ink-dim">
              Before you open a case — these may help
            </div>
            <ul className="space-y-2">
              {suggestions.map((a) => (
                <li key={a.id}>
                  <Link
                    className="text-sm font-medium text-accent-strong hover:underline"
                    to={`/app/faq#${a.id}`}
                  >
                    {a.title}
                  </Link>
                  <p className="text-xs text-ink-dim">{a.summary}</p>
                </li>
              ))}
            </ul>
          </div>
        )}
        <AttachmentPicker api={api} attachments={attachments} onChange={setAttachments} />
        <div className="flex justify-end">
          <button className="btn-primary" disabled={busy} onClick={() => void submit()}>
            {busy ? "Submitting…" : "Submit ticket"}
          </button>
        </div>
      </div>
    </Card>
  );
}

function TicketDetail({
  api,
  meEmail,
  ticketId,
  onBack,
}: {
  api: Api;
  meEmail: string;
  ticketId: string;
  onBack: () => void;
}) {
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reply, setReply] = useState("");
  const [attachments, setAttachments] = useState<TicketAttachmentRef[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .getTicket(ticketId)
      .then((r) => setTicket(r.ticket))
      .catch(() => setError("Could not load this ticket."));
  }, [api, ticketId]);

  async function sendReply() {
    if (reply.trim() === "" && attachments.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const { ticket } = await api.addTicketComment(ticketId, {
        body: reply.trim() === "" ? "(attachment)" : reply,
        attachments,
      });
      setTicket(ticket);
      setReply("");
      setAttachments([]);
    } catch {
      setError("Could not post your reply.");
    } finally {
      setBusy(false);
    }
  }

  async function download(objectPath: string) {
    try {
      const { url } = await api.ticketAttachmentUrl(ticketId, objectPath);
      window.location.assign(url);
    } catch {
      setError("Could not open that attachment.");
    }
  }

  if (error && !ticket) {
    return (
      <div>
        <button className="mb-4 text-sm text-ink-muted hover:text-ink" onClick={onBack}>
          ← Back to tickets
        </button>
        <Banner tone="error">{error}</Banner>
      </div>
    );
  }
  if (!ticket) return <p className="text-sm text-ink-muted">Loading ticket…</p>;

  const closed = ticket.status === "closed";

  return (
    <div>
      <button className="btn-ghost mb-4" onClick={onBack}>
        ← Back to tickets
      </button>
      <SectionTitle
        kicker={`Ticket${ticket.number === undefined ? "" : ` · Case #${ticket.number}`} · ${STATUS_LABEL[ticket.status]}`}
        title={ticket.subject}
        desc={`Opened ${new Date(ticket.createdAt).toLocaleString()} by ${
          ticket.requesterEmail === meEmail ? "you" : ticket.requesterEmail
        } · ${ticket.category} · ${ticket.priority} priority · age ${ageLabel(ticket.createdAt)}`}
      />

      {error && (
        <div className="mb-4">
          <Banner tone="error">{error}</Banner>
        </div>
      )}

      <Card className="mb-4">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <SlaBadge state={ticket.slaState} />
          <span className="badge">{ticket.status}</span>
          {ticket.tags?.map((tag) => (
            <span key={tag} className="badge">
              {tag}
            </span>
          ))}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-sm border border-line p-3">
            <div className="text-xs uppercase tracking-wider text-ink-dim">First response</div>
            {ticket.firstRespondedAt ? (
              <div className="mt-1 text-sm text-ink">Responded {new Date(ticket.firstRespondedAt).toLocaleString()}</div>
            ) : (
              <>
                <div className={`mt-1 text-sm ${slaTone(ticket.slaState)}`}>{dueLabel(ticket.firstResponseDueAt)}</div>
                {ticket.firstResponseDueAt && (
                  <div className="text-xs text-ink-dim">Due {new Date(ticket.firstResponseDueAt).toLocaleString()}</div>
                )}
              </>
            )}
          </div>
          <div className="rounded-sm border border-line p-3">
            <div className="text-xs uppercase tracking-wider text-ink-dim">Resolution</div>
            <div className={`mt-1 text-sm ${slaTone(ticket.slaState)}`}>{dueLabel(ticket.resolutionDueAt)}</div>
            {ticket.resolutionDueAt && (
              <div className="text-xs text-ink-dim">Due {new Date(ticket.resolutionDueAt).toLocaleString()}</div>
            )}
          </div>
        </div>
      </Card>

      <Card className="mb-4">
        <MarkdownBody rendered={ticket.renderedDescription} raw={ticket.description} />
        <AttachmentList
          attachments={ticket.attachments.filter(
            (a) => !ticket.comments.some((c) => c.attachments.some((ca) => ca.objectPath === a.objectPath)),
          )}
          onDownload={download}
        />
      </Card>

      <div className="space-y-3">
        {ticket.comments.map((c) => (
          <Card key={c.id} className={c.authorType === "staff" ? "border-accent/40" : ""}>
            <div className="mb-1 flex items-center gap-2 text-xs text-ink-dim">
              <span className={c.authorType === "customer" ? "badge" : "badge-accent"}>
                {c.authorType === "ai"
                  ? "AI Triage"
                  : c.authorType === "staff"
                    ? "PumaMesh support"
                    : c.authorEmail === meEmail
                      ? "You"
                      : "Teammate"}
              </span>
              <span>{c.authorEmail}</span>
              <span>· {new Date(c.createdAt).toLocaleString()}</span>
            </div>
            <MarkdownBody rendered={c.renderedBody} raw={c.body} />
            <AttachmentList attachments={c.attachments} onDownload={download} />
          </Card>
        ))}
      </div>

      <Card className="mt-4">
        {closed ? (
          <p className="text-sm text-ink-muted">This ticket is closed. Replying will re-open it.</p>
        ) : null}
        <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-ink-dim">
          Reply
        </label>
        <MentionTextarea
          api={api}
          className="input min-h-[90px]"
          value={reply}
          onChange={setReply}
          ticketId={ticket.id}
          placeholder="Add a reply for PumaMesh support. Tip: type @name to notify a teammate or PumaMesh."
        />
        <div className="mt-3">
          <AttachmentPicker api={api} attachments={attachments} onChange={setAttachments} />
        </div>
        <div className="mt-3 flex justify-end">
          <button className="btn-primary" disabled={busy} onClick={() => void sendReply()}>
            {busy ? "Sending…" : "Send reply"}
          </button>
        </div>
      </Card>
    </div>
  );
}

function AttachmentList({
  attachments,
  onDownload,
}: {
  attachments: {
    objectPath: string;
    filename: string;
    sizeBytes: number;
    scanStatus?: "pending" | "clean" | "infected";
  }[];
  onDownload: (objectPath: string) => void;
}) {
  if (attachments.length === 0) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {attachments.map((a) => {
        const blocked = a.scanStatus === "infected" || a.scanStatus === "pending";
        const stateLabel =
          a.scanStatus === "infected"
            ? "blocked"
            : a.scanStatus === "pending"
              ? "scanning"
              : "clean";
        return (
          <button
            key={a.objectPath}
            className={`badge ${blocked ? "cursor-not-allowed opacity-60" : "hover:text-accent-strong"}`}
            disabled={blocked}
            onClick={() => onDownload(a.objectPath)}
          >
            ↓ {a.filename} ({formatBytes(a.sizeBytes)}) · {stateLabel}
          </button>
        );
      })}
    </div>
  );
}

function AttachmentPicker({
  api,
  attachments,
  onChange,
}: {
  api: Api;
  attachments: TicketAttachmentRef[];
  onChange: (refs: TicketAttachmentRef[]) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const added: TicketAttachmentRef[] = [];
      for (const file of Array.from(files)) {
        const { url, objectPath } = await api.uploadUrl(file.name, file.type);
        await uploadToSignedUrl(url, file, () => {});
        added.push({ objectPath, filename: file.name });
      }
      onChange([...attachments, ...added]);
    } catch (err) {
      setError(`Attachment upload failed: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-ink-dim">
        Attach logs / files
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <label className="btn-ghost cursor-pointer">
          {busy ? "Uploading…" : "Add file"}
          <input
            type="file"
            multiple
            className="hidden"
            disabled={busy}
            onChange={(e) => void handleFiles(e.target.files)}
          />
        </label>
        {attachments.map((a) => (
          <span key={a.objectPath} className="badge">
            {a.filename}
          </span>
        ))}
      </div>
      {error && <p className="mt-1 text-xs text-danger">{error}</p>}
    </div>
  );
}
